import type { EntryStatus, EntryV2 } from "@/shared/cache";
import { createAction, type QueuedAction } from "@/v2/hooks/useActionQueue";
import { formatEntryId } from "./entryId";
import {
	type ActionDeps,
	ApiError,
	actingHeaders,
	apiCall,
	plural,
	refreshBoth,
} from "./types";

/**
 * Targets that the cheap-path PATCH /v2/entries/{id} accepts. `"exported"` is
 * intentionally excluded — the Phase 1 backend returns 500 NOT_IMPLEMENTED for
 * that branch (export is wired in Phase 2), and the existing v1
 * `/records/exportRecords` flow is still the right path for now. Callers that
 * want to export should keep using the export action / `useExportEntries`
 * hook.
 */
export type SetStatusTarget = Exclude<EntryStatus, "exported">;

/**
 * Optimistically apply a status transition to the v2 cache row. Returns the
 * next entries array. Rules:
 *
 *  - `"deleted"`: row leaves the cache (the entry still exists in
 *    `deleted_transactions` server-side, but the live working set treats it
 *    as gone — same UX as the previous v1 `/records/delete` flow).
 *  - `"reviewed"` / `"tracked"`: status field flips in place; no row reorder.
 *
 * If the row isn't in the cache (already moved by a refetch), the update is
 * a no-op — the rollback returned by `optimisticUpdate` still restores the
 * original snapshot on failure.
 */
function applyOptimisticStatus(
	prev: EntryV2[],
	id: string,
	target: SetStatusTarget,
): EntryV2[] {
	if (target === "deleted") {
		return prev.filter((e) => e.id !== id);
	}
	return prev.map((e) => (e.id === id ? { ...e, status: target } : e));
}

/**
 * PATCH /v2/entries/ent_<id> { status: target }. The Phase 1 backend handles
 * `tracked ↔ reviewed`, `any → deleted`, `deleted → tracked|reviewed`,
 * and edit-on-deleted in one endpoint; this action just wraps the wire call
 * and the optimistic update on the `entries_v2` cache.
 *
 * Server-side error codes the client should expect:
 *  - `ENTRY_EXPORTED` (403) — caller tried to mutate an exported row. The
 *    popover already hides this transition, so this is defense-in-depth.
 *  - `ENTRY_NOT_FOUND` (404) — row gone, or `superseded = 1`. Rollback
 *    restores; a refetch will reconcile.
 *
 * Optimistic update targets the v2 cache only — the legacy DataProvider /
 * EntriesContext stack has no `status` concept on its row shape, so there's
 * nothing to mirror there. The post-action refresh still hits both stores so
 * other pages see the change.
 */
export function setStatusAction(
	id: number,
	target: SetStatusTarget,
	deps: ActionDeps,
	onApiError?: (err: ApiError) => void,
	timekeeperId?: string | null,
): QueuedAction {
	const entryId = formatEntryId(id);
	const rollback =
		deps.updateEntriesV2?.((prev) =>
			applyOptimisticStatus(prev, entryId, target),
		) ?? null;

	return createAction({
		execute: async () => {
			try {
				await apiCall(deps, {
					endpoint: `/v2/entries/${entryId}`,
					method: "PATCH",
					body: { status: target },
					headers: actingHeaders(timekeeperId),
				});
			} catch (err) {
				if (err instanceof ApiError) onApiError?.(err);
				throw err;
			}
		},
		rollback,
		toast: {
			loading:
				target === "deleted"
					? "Deleting entry…"
					: target === "reviewed"
						? "Marking reviewed…"
						: "Marking tracked…",
			success:
				target === "deleted"
					? "Entry deleted"
					: target === "reviewed"
						? "Entry marked reviewed"
						: "Entry marked tracked",
			error: (err) => {
				if (err instanceof ApiError) {
					if (err.code === "ENTRY_EXPORTED")
						return "Exported entries can't be changed";
					if (err.code === "ENTRY_NOT_FOUND") return "Entry no longer exists";
				}
				return target === "deleted"
					? "Failed to delete entry"
					: "Failed to update status";
			},
		},
		onSuccess: () => refreshBoth(deps),
		onError: () => refreshBoth(deps),
	});
}

/**
 * Bulk status transition — one queued action, one network call, one toast for
 * the batch. Used by /entries' toolbar delete (and any future multi-select
 * transitions) so the UI doesn't show N toasts for a single user gesture.
 *
 * Wire calls (set-based bulk endpoints):
 *  - `"reviewed"` / `"tracked"` → POST /v2/entries/review
 *    `{ ids, reviewed: boolean }`
 *  - `"deleted"` → POST /v2/entries/delete `{ ids }`
 *
 * Both endpoints are atomic: any invalid id (404 ENTRY_NOT_FOUND, 403
 * ENTRY_EXPORTED) rolls back the entire batch server-side, so the action
 * either fully lands or fully fails — no partial-success summary. On failure,
 * the optimistic rollback restores the full batch and the post-action refresh
 * reconciles per the server's actual state.
 *
 * Note: unlike the single-entry PATCH, POST /entries/review does NOT restore
 * deleted entries (they 404). The multi-select UI already hides invalid
 * transitions, so this is defense-in-depth, not a UX change.
 */
export function setStatusManyAction(
	ids: number[],
	target: SetStatusTarget,
	deps: ActionDeps,
	onApiError?: (err: ApiError) => void,
	timekeeperId?: string | null,
): QueuedAction {
	const v2Ids = [...new Set(ids.map(formatEntryId))];
	const rollback =
		deps.updateEntriesV2?.((prev) => {
			let next = prev;
			for (const id of v2Ids) next = applyOptimisticStatus(next, id, target);
			return next;
		}) ?? null;

	const n = ids.length;
	const verbing =
		target === "deleted"
			? "Deleting"
			: target === "reviewed"
				? "Marking reviewed"
				: "Marking tracked";
	const verbed =
		target === "deleted"
			? "Deleted"
			: target === "reviewed"
				? "Marked reviewed"
				: "Marked tracked";

	return createAction({
		execute: async () => {
			try {
				await apiCall(
					deps,
					target === "deleted"
						? {
								endpoint: "/v2/entries/delete",
								method: "POST",
								body: { ids: v2Ids },
								headers: actingHeaders(timekeeperId),
							}
						: {
								endpoint: "/v2/entries/review",
								method: "POST",
								body: { ids: v2Ids, reviewed: target === "reviewed" },
								headers: actingHeaders(timekeeperId),
							},
				);
			} catch (err) {
				if (err instanceof ApiError) onApiError?.(err);
				throw err;
			}
		},
		rollback,
		toast: {
			loading: `${verbing} ${n} ${plural(n)}…`,
			success: `${verbed} ${n} ${plural(n)}`,
			error: (err) => {
				if (err instanceof ApiError) {
					if (err.code === "ENTRY_EXPORTED")
						return "Some entries are exported — nothing was changed";
					if (err.code === "ENTRY_NOT_FOUND")
						return "Some entries no longer exist — nothing was changed";
				}
				return target === "deleted"
					? "Failed to delete entries"
					: "Failed to update entries";
			},
		},
		onSuccess: () => refreshBoth(deps),
		onError: () => refreshBoth(deps),
	});
}
